<template>
  <AppLayout>
    <div class="mx-auto max-w-[1800px] pt-2 md:pt-6">
      <header
        class="flex flex-wrap items-end justify-between gap-x-8 gap-y-4 border-b border-boba-grey/50 pb-5"
      >
        <div>
          <p
            class="text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-boba-grey-dark"
          >
            Blokpax
          </p>
          <h1
            class="mt-1 text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-boba-brown"
          >
            Special Auctions
          </h1>
          <p class="mt-2 max-w-2xl text-sm md:text-base text-boba-grey-dark">
            Sealed-bid and falling-price auctions for sealed product and
            exclusives, paid in miles.
          </p>
        </div>

        <nav
          aria-label="Auction status"
          class="inline-flex rounded-lg border border-boba-grey/60 bg-boba-sand-light p-1 text-sm font-medium"
        >
          <router-link
            v-for="tab in tabs"
            :key="tab.id"
            :to="tab.to"
            :aria-current="currentTab === tab.id ? 'page' : undefined"
            class="rounded-md px-4 py-1.5 transition-colors"
            :class="
              currentTab === tab.id
                ? 'bg-white text-boba-brown shadow-sm ring-1 ring-black/5'
                : 'text-boba-grey-dark hover:text-boba-brown'
            "
          >
            {{ tab.label }}
          </router-link>
        </nav>
      </header>

      <HeadlessIndex :state="auctionState" v-slot="{ listings, loaded }">
        <div
          v-if="!loaded"
          class="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
          aria-busy="true"
          aria-label="Loading auctions"
        >
          <div
            v-for="n in 4"
            :key="`skeleton-${n}`"
            class="animate-pulse overflow-hidden rounded-xl border border-boba-grey/50 bg-white shadow-sm"
          >
            <div class="aspect-[4/3] bg-boba-sand-light" />
            <div class="space-y-3 p-5">
              <div class="h-3 w-1/3 rounded bg-boba-sand" />
              <div class="h-5 w-3/4 rounded bg-boba-sand" />
              <div class="h-4 w-1/2 rounded bg-boba-sand-light" />
              <div class="mt-6 h-10 rounded-lg bg-boba-sand-light" />
            </div>
          </div>
        </div>

        <template v-else-if="listings.length">
          <p class="mt-5 text-sm text-boba-grey-dark tabular-nums">
            {{ listings.length }}
            {{ currentTab === "active" ? "open" : "past" }}
            {{ listings.length === 1 ? "auction" : "auctions" }}
          </p>
          <div
            class="mt-3 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
          >
            <listing-card
              v-for="listing in sorted(listings)"
              :key="`listing-${listing.slug}`"
              :listing="listing"
            />
          </div>
        </template>

        <div
          v-else
          class="mt-6 rounded-xl border border-dashed border-boba-grey bg-white/60 px-6 py-14 text-center"
        >
          <i
            class="fa-light fa-gavel text-3xl text-boba-grey-dark"
            aria-hidden="true"
          />
          <p class="mt-3 text-base font-medium text-boba-brown">
            {{
              currentTab === "active"
                ? "No auctions are open right now."
                : "No auctions have ended yet."
            }}
          </p>
          <p class="mt-1 text-sm text-boba-grey-dark">
            {{
              currentTab === "active"
                ? "Check back soon, or browse past results."
                : "Once an auction closes it will show up here."
            }}
          </p>
          <p class="mt-5">
            <router-link
              :to="currentTab === 'active' ? tabs[1].to : tabs[0].to"
              class="inline-block rounded-lg border border-boba-brown/20 px-5 py-2 text-sm font-semibold text-boba-brown hover:bg-boba-sand-light"
            >
              {{ currentTab === "active" ? "View past auctions" : "View open auctions" }}
            </router-link>
          </p>
        </div>
      </HeadlessIndex>
    </div>
  </AppLayout>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import AppLayout from "@/components/Layouts/Application.vue";
import HeadlessIndex from "@/components/Headless/Index.vue";
import ListingCard from "@/components/Listing/Card.vue";
import { Listing } from "@/types";

const route = useRoute();

const tabs = [
  { id: "active", label: "Open", to: { name: "listing-index" } },
  { id: "ended", label: "Ended", to: { name: "listing-index", query: { tab: "ended" } } },
] as const;

type Tab = (typeof tabs)[number]["id"];

const currentTab = ref<Tab>(
  tabs.some((t) => t.id === route.query.tab) ? (route.query.tab as Tab) : "active",
);

const auctionState = computed(() => {
  return currentTab.value === "active" ? ["pending", "active"] : ["ended", "complete"];
});

/**
 * Open tab: whatever is closing or opening soonest first, live before
 * upcoming. Ended tab: most recently closed first.
 */
function sorted(listings: Listing[]): Listing[] {
  const at = (l: Listing) =>
    (l.countdownTarget() ?? l.endsAt() ?? l.startsAt()).toMillis();

  if (currentTab.value === "ended") {
    return [...listings].sort((a, b) => at(b) - at(a));
  }

  const rank = (l: Listing) => (l.isActive() ? 0 : 1);
  return [...listings].sort((a, b) => rank(a) - rank(b) || at(a) - at(b));
}
</script>
